import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Sidebar from './sidebar';
import PaymentStatusProductPositions from './modal_windows/PaymentStatusProductPositions';

import {
  StatCard, RefreshButton, SearchField, FilterPill,
  StatusBadge, TableCard, LoadingState, EmptyState, Pagination,
} from '../../ui/CrmPage';

const PAGE_SIZE = 15;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    timeZone: 'Europe/Moscow',
  });
};

const ageBucket = (deliveryDate) => {
  if (!deliveryDate) return 'unknown';
  const diff = Math.floor((Date.now() - new Date(deliveryDate)) / 86400000);
  if (diff <= 7)  return 'fresh';
  if (diff <= 21) return 'warning';
  if (diff <= 30) return 'overdue';
  return 'danger';
};

const PaymentStatus = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Все');
  const [regionFilter, setRegionFilter] = useState('Все');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState({});

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}v1/orders/paid-status`);
      setOrders(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Ошибка:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetails = (orderId) => {
    fetch(`${process.env.REACT_APP_API_URL}v1/order_orderitems/${orderId}`)
      .then(response => response.json())
      .then(data => setOrderDetails(prev => ({ ...prev, items: data })))
      .catch(error => console.error('Ошибка:', error));
  };

  const summary = useMemo(() => {
    const total = orders.length;
    const paid = orders.filter(o => /оплачен/i.test(o.psname || '')).length;
    const waiting = orders.filter(o => /ожидает/i.test(o.psname || '')).length;
    const overdue = orders.filter(o => ageBucket(o.delivery_date) === 'danger').length;
    return { total, paid, waiting, overdue };
  }, [orders]);

  const psGroups = useMemo(() =>
    Array.from(new Set(orders.map(o => o.psname).filter(Boolean))), [orders]);

  const regionGroups = useMemo(() =>
    Array.from(new Set(orders.map((o) => o.region_name).filter(Boolean))).sort(), [orders]);

  const filteredOrders = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return orders.filter(o => {
      if (statusFilter !== 'Все' && o.psname !== statusFilter) return false;
      if (regionFilter !== 'Все' && (o.region_name || '') !== regionFilter) return false;
      if (!term) return true;
      return (
        (o.client_name || '').toLowerCase().includes(term) ||
        (o.delivery_address || '').toLowerCase().includes(term) ||
        String(o.order_number || '').includes(term)
      );
    });
  }, [orders, searchTerm, statusFilter, regionFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const paginatedOrders = filteredOrders.slice(pageStart, pageStart + PAGE_SIZE);

  const resetFilters = () => { setSearchTerm(''); setStatusFilter('Все'); setRegionFilter('Все'); setCurrentPage(1); };
  const hasFilters = searchTerm || statusFilter !== 'Все' || regionFilter !== 'Все';

  return (
    <div className="payment-status-container crmpage">
      <div className="payment-status-sidebar">
        <Sidebar />
      </div>

      <div className="payment-status-basic-container crmpage__main">
        <header className="crmpage__head">
          <h1 className="crmpage__title">Статус оплаты</h1>
        </header>

        <section className="crmpage__stats">
          <StatCard label="Всего"     value={summary.total}   accent="neutral" />
          <StatCard label="Оплачены"  value={summary.paid}    accent="success" />
          <StatCard label="Ожидают"   value={summary.waiting} accent="warning" />
          <StatCard label="Просрочены" value={summary.overdue} accent="danger" />
          <RefreshButton onClick={() => setReloadKey(k => k + 1)} />
        </section>

        <section className="crmpage__filters">
          <SearchField
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Поиск: номер, клиент, адрес…"
          />
          <div className="crmpage__pills">
            <FilterPill active={statusFilter === 'Все'}
              onClick={() => { setStatusFilter('Все'); setCurrentPage(1); }}
              count={orders.length}>Все</FilterPill>
            {psGroups.map(s => (
              <FilterPill key={s}
                active={statusFilter === s}
                onClick={() => { setStatusFilter(s); setCurrentPage(1); }}
                count={orders.filter(o => o.psname === s).length}>{s}</FilterPill>
            ))}
          </div>
          {hasFilters && (
            <button type="button" className="crmpage__reset" onClick={resetFilters}>Сбросить</button>
          )}
        </section>

        <section className="crmpage__filters crmpage__filters--regions">
          <div className="crmpage__pills">
            <FilterPill
              active={regionFilter === 'Все'}
              onClick={() => { setRegionFilter('Все'); setCurrentPage(1); }}
              count={orders.length}
            >Все регионы</FilterPill>
            {regionGroups.map((r) => (
              <FilterPill
                key={r}
                active={regionFilter === r}
                onClick={() => { setRegionFilter(r); setCurrentPage(1); }}
                count={orders.filter((o) => (o.region_name || '') === r).length}
              >{r}</FilterPill>
            ))}
          </div>
        </section>

        <TableCard>
          {loading ? (
            <LoadingState text="Загружаем статусы оплат…" />
          ) : paginatedOrders.length === 0 ? (
            <EmptyState title="Нет заказов" text={hasFilters ? 'Сбросьте фильтры.' : 'Здесь будут заказы с информацией об оплате.'} />
          ) : (
            <>
              <div className="crmpage__table-wrap">
                <table className="crmpage__table">
                  <colgroup>
                    <col style={{ width: 80 }} />
                    <col />
                    <col />
                    <col style={{ width: 160 }} />
                    <col style={{ width: 140 }} />
                    <col style={{ width: 110 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Номер</th>
                      <th>Клиент</th>
                      <th className="crmpage__hide-880">Адрес доставки</th>
                      <th>Статус заказа</th>
                      <th>Статус оплаты</th>
                      <th className="crmpage__hide-1000">Доставка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedOrders.map((order, idx) => (
                      <tr
                        key={idx}
                        className="crmpage__row"
                        onClick={() => { fetchOrderDetails(order.id_orders); setSelectedOrder([order]); }}
                      >
                        <td><span className="crmpage__num">№{order.order_number}</span></td>
                        <td>
                          <div className="crmpage__primary">{order.client_name || '—'}</div>
                          <div className="crmpage__sub">{order.region_name}</div>
                        </td>
                        <td className="crmpage__muted crmpage__hide-880">{order.delivery_address || '—'}</td>
                        <td><StatusBadge name={order.status_name} /></td>
                        <td><StatusBadge name={order.psname} /></td>
                        <td className="crmpage__date crmpage__hide-1000">{formatDate(order.delivery_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="crmpage__cards">
                {paginatedOrders.map((o, idx) => (
                  <div key={idx} className="crmpage__card"
                    onClick={() => { fetchOrderDetails(o.id_orders); setSelectedOrder([o]); }}>
                    <div className="crmpage__card-head">
                      <span className="crmpage__num">№{o.order_number}</span>
                      <StatusBadge name={o.psname} />
                    </div>
                    <div className="crmpage__card-row">
                      <span className="crmpage__card-label">Клиент</span>
                      <span className="crmpage__card-val">{o.client_name || '—'}</span>
                    </div>
                    <div className="crmpage__card-row">
                      <span className="crmpage__card-label">Статус</span>
                      <span className="crmpage__card-val"><StatusBadge name={o.status_name} /></span>
                    </div>
                    <div className="crmpage__card-row">
                      <span className="crmpage__card-label">Доставка</span>
                      <span className="crmpage__card-val">{formatDate(o.delivery_date)}</span>
                    </div>
                  </div>
                ))}
              </div>

              <Pagination current={currentPage} total={totalPages} onChange={setCurrentPage} />
            </>
          )}
        </TableCard>
      </div>

      {selectedOrder && (
        <PaymentStatusProductPositions
          orders={selectedOrder}
          oDetails={orderDetails}
          onClose={() => setSelectedOrder(null)}
          clientName={selectedOrder[0]?.client_name}
        />
      )}
    </div>
  );
};

export default PaymentStatus;
